/**
 * Menu component
 *
 * @module component-menu
 * @version 1.0.0
 * @extends HTMLElement
 */

customElements.component = "component-menu";

if (!customElements.get(customElements.component)) {
  class ComponentMenu extends HTMLElement {
    /**
     * Constructor
     */
    constructor() {
      super();
      this.previousActiveElement = null;
      this.submenuTraps = new Map();
      this.desktopFocusOutHandlers = new Map();
    }

    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;
      this.dataset.activeLevel = 1;
      this.initEvents();
    }

    initEvents() {
      // Desktop: pre-position all submenus before any hover to avoid first-paint glitch.
      // Recompute on resize and after fonts load (layout may shift).
      this.positionAllSubmenus();
      requestAnimationFrame(() => this.positionAllSubmenus());
      window.addEventListener("resize", () => this.positionAllSubmenus());
      if (document.fonts?.ready) {
        document.fonts.ready.then(() => this.positionAllSubmenus());
      }

      // Re-measure on hover as a safety net for any late layout shifts.
      // Delegated via mouseover (mouseenter doesn't bubble) so it works
      // regardless of when individual <li> nodes were created.
      this.addEventListener("mouseover", (event) => {
        const li = event.target.closest('li[data-level="1"]');
        if (!li || !this.contains(li)) return;
        const submenu = li.querySelector(":scope > .submenu");
        if (submenu) this.positionSubmenu(submenu);
      });

      // Open/toggle submenu (click again to close)
      this.querySelectorAll("[data-open-submenu]").forEach((button) => {
        button.addEventListener("click", () => {
          const submenuId = button.dataset.openSubmenu;
          const submenu = document.querySelector(`[data-submenu="${submenuId}"]`);
          if (submenu?.classList.contains("active")) {
            this.closeSubmenu(submenuId);
          } else {
            this.openSubmenu(submenuId);
          }
        });
      });

      // Close submenu
      this.querySelectorAll("[data-close-submenu]").forEach((button) => {
        button.addEventListener("click", () => {
          this.closeSubmenu(button.dataset.closeSubmenu);
        });
      });

      // Toggle submenu (for third level)
      this.querySelectorAll("[data-toggle-submenu]").forEach((button) => {
        button.addEventListener("click", () => {
          this.toggleSubmenu(button.dataset.toggleSubmenu);
        });
      });

      // Handle escape key for submenus
      this.addEventListener("keydown", (e) => {
        if (e.key === "Escape") {
          const activeSubmenu = this.querySelector(".submenu.active");
          if (activeSubmenu) {
            const submenuId = activeSubmenu.dataset.submenu;
            // Only handle escape for level 2 submenus (those with data-open-submenu)
            if (this.querySelector(`[data-open-submenu="${submenuId}"]`)) {
              this.closeSubmenu(submenuId);
              e.preventDefault();
            }
          }
        }

        // Desktop: explicit Tab/Shift+Tab handling when submenu is open (fixes focus getting stuck)
        if (e.key === "Tab" && this.isDesktop()) {
          const activeSubmenu = this.querySelector(".submenu.active");
          if (activeSubmenu) {
            const submenuId = activeSubmenu.dataset.submenu;
            const button = this.querySelector(`[data-open-submenu="${submenuId}"]`);
            const focusables = this.getFocusableElements(activeSubmenu);
            const firstSubmenuFocusable = focusables[0];

            if (document.activeElement === button && !e.shiftKey) {
              // Tab from button: move to first submenu link
              if (firstSubmenuFocusable) {
                e.preventDefault();
                firstSubmenuFocusable.focus();
              }
            } else if (document.activeElement === firstSubmenuFocusable && e.shiftKey) {
              // Shift+Tab from first submenu link: move back to button
              if (button) {
                e.preventDefault();
                button.focus();
              }
            }
          }
        }
      });
    }

    /**
     * Get all focusable elements within a container
     * @param {HTMLElement} container - The container to search within
     * @returns {Array} Array of focusable elements
     */
    getFocusableElements(container = this) {
      return Array.from(
        container.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter(
        (el) => !el.hasAttribute("disabled") && el.offsetParent !== null
      );
    }

    /**
     * Create a focus trap for a submenu
     * @param {HTMLElement} submenu - The submenu element
     * @param {string} submenuId - The submenu ID
     * @returns {Object} The focus trap instance
     */
    createSubmenuTrap(submenu, submenuId) {
      // Create a new focus trap for the submenu
      const submenuTrap = focusTrap.createFocusTrap(submenu, {
        escapeDeactivates: false, // We handle escape key ourselves
        allowOutsideClick: true,
        returnFocusOnDeactivate: true,
        fallbackFocus: submenu,
        onDeactivate: () => {
          // Clean up when deactivated
          this.submenuTraps.delete(submenuId);
        },
      });

      this.submenuTraps.set(submenuId, submenuTrap);
      return submenuTrap;
    }

    /**
     * Pre-position every level-1 submenu so the offset is set before first hover.
     * Prevents the glitch where the submenu paints overflowing for one frame
     * before mouseenter measurement runs.
     */
    positionAllSubmenus() {
      if (!this.isDesktop()) return;
      this.querySelectorAll('li[data-level="1"] > .submenu').forEach((submenu) => {
        this.positionSubmenu(submenu);
      });
    }

    /**
     * Auto-position a submenu so it doesn't overflow the viewport on desktop.
     * Writes inline `transform` directly (rather than mutating a CSS custom
     * property) because Safari does not always recompute transforms
     * synchronously when --submenu-offset-x changes, causing the submenu to
     * paint at the un-adjusted position.
     */
    positionSubmenu(submenu) {
      if (!this.isDesktop()) return;

      // Submenu is display:none on desktop until hover. Make it briefly
      // measurable (off-screen via the default -100px transform) without
      // letting it paint by toggling display synchronously around the read.
      const prevDisplay = submenu.style.display;
      submenu.style.transform = "translateX(-100px)";
      submenu.style.display = "block";
      void submenu.offsetWidth;

      const rect = submenu.getBoundingClientRect();
      submenu.style.display = prevDisplay;
      const viewportWidth = document.documentElement.clientWidth;
      const padding = 20;
      let offset = -100;

      if (rect.right > viewportWidth - padding) {
        const overflow = rect.right - (viewportWidth - padding);
        offset = -100 - overflow;
      } else if (rect.left < padding) {
        const shift = padding - rect.left;
        offset = -100 + shift;
      }

      submenu.style.transform = `translateX(${offset}px)`;
    }

    /**
     * Reset submenu position to CSS defaults.
     */
    resetSubmenuPosition(submenu) {
      submenu.style.transform = "";
    }

    open() {
      this.classList.add("active");
    }

    close() {
      // Remove desktop focusout handlers
      this.desktopFocusOutHandlers.forEach((handler, submenuId) => {
        const submenu = this.querySelector(`[data-submenu="${submenuId}"]`);
        const parentLi = submenu?.closest("li[data-level='1']");
        if (parentLi) {
          parentLi.removeEventListener("focusout", handler);
        }
      });
      this.desktopFocusOutHandlers.clear();

      // Deactivate any active submenu traps
      this.submenuTraps.forEach((trap) => trap.deactivate());
      this.submenuTraps.clear();

      this.classList.remove("active");

      // Remove all aria-hidden attributes
      this.querySelectorAll("[aria-hidden]").forEach((el) => {
        el.removeAttribute("aria-hidden");
      });
    }

    isDesktop() {
      return window.matchMedia("(min-width: 1024px)").matches;
    }

    openSubmenu(submenuId) {
      const submenu = document.querySelector(`[data-submenu="${submenuId}"]`);
      const button = this.querySelector(`[data-open-submenu="${submenuId}"]`);
      const parentLi = submenu?.closest("li[data-level='1']");

      if (submenu) {
        this.classList.add("loading");

        setTimeout(() => {
          submenu.classList.add("active");
          this.dataset.activeLevel++;
          this.classList.remove("loading");

          // Update aria-expanded
          if (button) {
            button.setAttribute("aria-expanded", "true");
          }

          if (this.isDesktop()) {
            // Auto-position to prevent viewport overflow
            this.positionSubmenu(submenu);

            // Desktop: dropdown behavior - no focus trap, close when focus leaves
            const firstFocusable = submenu.querySelector(
              'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
            );
            if (firstFocusable) {
              firstFocusable.focus();
            }

            const handleFocusOut = () => {
              requestAnimationFrame(() => {
                const activeEl = document.activeElement;
                const isInsideSubmenu = submenu.contains(activeEl);
                const isOpenButton = button?.contains(activeEl);
                const isInsideParentLi = parentLi?.contains(activeEl);

                if (!isInsideSubmenu && !isOpenButton && !isInsideParentLi) {
                  // Don't steal focus - user is navigating away with Tab/Shift+Tab
                  this.closeSubmenu(submenuId, { returnFocus: false });
                }
              });
            };

            this.desktopFocusOutHandlers.set(submenuId, handleFocusOut);
            parentLi?.addEventListener("focusout", handleFocusOut);
          } else {
            // Mobile: full-screen submenu with focus trap
            const submenuTrap = this.createSubmenuTrap(submenu, submenuId);
            submenuTrap.activate();

            const firstFocusable = submenu.querySelector(
              'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
            );
            if (firstFocusable) {
              firstFocusable.focus();
            }
          }
        }, 300);
      }
    }

    closeSubmenu(submenuId, options = {}) {
      const { returnFocus = true } = options;
      const submenu = document.querySelector(`[data-submenu="${submenuId}"]`);
      const button = this.querySelector(`[data-open-submenu="${submenuId}"]`);
      const parentLi = submenu?.closest("li[data-level='1']");

      if (submenu) {
        this.classList.add("loading");

        // Reset submenu position
        this.resetSubmenuPosition(submenu);

        // Remove desktop focusout handler if present
        const focusOutHandler = this.desktopFocusOutHandlers.get(submenuId);
        if (focusOutHandler && parentLi) {
          parentLi.removeEventListener("focusout", focusOutHandler);
          this.desktopFocusOutHandlers.delete(submenuId);
        }

        // Deactivate the focus trap for this submenu
        const submenuTrap = this.submenuTraps.get(submenuId);
        if (submenuTrap) {
          submenuTrap.deactivate();
        }

        setTimeout(() => {
          this.classList.remove("loading");
          submenu.classList.remove("active");
          this.dataset.activeLevel--;

          // Update aria-expanded
          if (button) {
            button.setAttribute("aria-expanded", "false");
          }

          // Only return focus when explicitly closing (Escape, close button), not when navigating away
          if (returnFocus && button) {
            button.focus();
          }
        }, 300);
      }
    }

    toggleSubmenu(submenuId) {
      const submenu = document.querySelector(`[data-submenu="${submenuId}"]`);
      const button = this.querySelector(`[data-toggle-submenu="${submenuId}"]`);

      if (submenu) {
        // Simple toggle for third level - no focus trap needed
        const isActive = submenu.classList.toggle("active");

        // Update aria-expanded
        if (button) {
          button.setAttribute("aria-expanded", isActive ? "true" : "false");
        }
      }
    }
  }

  customElements.define(customElements.component, ComponentMenu);
}
